import { useEffect, useRef } from 'react'
import { isAxiosError } from 'axios'
import { apiClient } from '@/infrastructure/api/client'
import { useAuthStore } from '@/application/stores/auth.store'

const SESSION_CHECK_INTERVAL_MS = 10_000

/**
 * Polls GET /auth/session every 10 s via the shared Axios client.
 * The Axios response interceptor automatically attempts a token refresh on 401.
 * If the refresh succeeds, the session check resolves transparently.
 * If the refresh fails (or there is no refresh token), the interceptor calls
 * logout() and the request rejects with a 401 — at that point `onUnauthorized`
 * is invoked so the caller can redirect or reload.
 * All other errors (network, 5xx) are silently ignored here.
 *
 * Only active while the user is authenticated.
 */
export function useSessionWatcher(onUnauthorized: () => void): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const accessToken = useAuthStore((state) => state.accessToken)

  // Stable ref — interval closure always calls the latest callback
  const onUnauthorizedRef = useRef(onUnauthorized)
  onUnauthorizedRef.current = onUnauthorized

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return

    const intervalId = setInterval(() => {
      apiClient.get('/auth/session', { _skipErrorToast: true }).catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 401) {
          // The interceptor already called logout(). Trigger redirect/reload.
          onUnauthorizedRef.current()
        }
        // Other errors (network, 5xx) are silently ignored
      })
    }, SESSION_CHECK_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [isAuthenticated, accessToken])
}
